// NZ wall-clock ↔ UTC primitives. Pure and dependency-free so they can be
// imported anywhere (no React). These convert between an
// ``<input type="datetime-local">`` string (which carries no zone) and the
// absolute UTC instant the backend stores, treating the input as a
// Pacific/Auckland wall-clock time per the repo convention (CLAUDE.md:
// convert to NZ-local only at the presentation layer).
//
// Extracted verbatim from DialpadReconciliationTile so other surfaces (e.g.
// the dashboard time-range picker) can share one correct implementation.

export const NZ_TIME_ZONE = "Pacific/Auckland";

const NZ_WALL_TIME_FMT = new Intl.DateTimeFormat("en-NZ", {
  timeZone: NZ_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// Today's date in YYYY-MM-DD (Pacific/Auckland). Used by date pickers as
// both the default end of a freshly-opened picker and the max-allowed end
// bound. Extracted from TimeRangeControls so single-date pickers (e.g. the
// Generate Sales Scenarios window end date) share one implementation.
export function todayNzIso(): string {
  const fmt = new Intl.DateTimeFormat("en-NZ", {
    timeZone: NZ_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// Parse a ``YYYY-MM-DDTHH:mm`` datetime-local string into the UTC ms it
// would represent if the wall-clock fields were read as UTC. This is an
// intermediate "wall time as ms" value, not the final instant — it lets us
// compare wall-clock fields numerically. Returns null on malformed input.
export function parseDatetimeLocalWallMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const [, year, month, day, hour, minute] = m;
  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
}

// Given an absolute instant (UTC ms), return the "wall time as ms" of how
// that instant reads on a Pacific/Auckland clock. Pairs with
// parseDatetimeLocalWallMs so the two can be differenced to find the offset.
export function wallMsInNz(ms: number): number {
  const parts = NZ_WALL_TIME_FMT.formatToParts(new Date(ms));
  const get = (type: string) => {
    const value = parts.find((p) => p.type === type)?.value;
    return value == null ? NaN : Number(value);
  };
  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
  );
}

// Convert a datetime-local string interpreted as NZ wall-clock time into the
// absolute UTC instant (ms) it denotes. Iterates to converge on the offset
// (NZST = UTC+12, NZDT = UTC+13) so DST transitions resolve correctly: the
// loop runs a few times because the NZ offset itself can shift across the
// candidate instant near a "spring forward" / "fall back" boundary. Returns
// null on malformed input.
export function parseNzDatetimeLocalAsUtcMs(value: string): number | null {
  const targetWallMs = parseDatetimeLocalWallMs(value);
  if (targetWallMs == null) return null;

  let utcMs = targetWallMs;
  for (let i = 0; i < 3; i += 1) {
    const renderedWallMs = wallMsInNz(utcMs);
    if (!Number.isFinite(renderedWallMs)) return null;
    const delta = targetWallMs - renderedWallMs;
    if (delta === 0) return utcMs;
    utcMs += delta;
  }
  return utcMs;
}
